import type { NavigateFunction } from 'react-router';

interface DateFilterParams {
	value: string | string[];
	operator: string;
	filterType: 'created_at';
}

/**
 * Creates URL search params with date filters for AI chat analytics
 */
export function createAiChatFilters(filters: DateFilterParams[]): string {
	const searchParams = new URLSearchParams();
	searchParams.set('filters', JSON.stringify(filters));
	return searchParams.toString();
}

/**
 * Navigates to AI chat analytics with date filters based on timeseries data point
 */
export function navigateToAiChatsWithDateFilter(
	navigate: NavigateFunction,
	dataIndex: number,
	timeseriesPoints: Array<{ timestamp: string }> | undefined
): void {
	if (
		!timeseriesPoints ||
		dataIndex < 0 ||
		dataIndex >= timeseriesPoints.length
	) {
		return;
	}

	const point = timeseriesPoints[dataIndex];

	// Convert timestamp to date and subtract one day since API returns start of next day
	const pointDate = new Date(point.timestamp);
	pointDate.setDate(pointDate.getDate() - 1);
	const [filterValue] = pointDate.toISOString().split('T');

	const filters: DateFilterParams[] = [
		{
			value: filterValue,
			operator: 'is on or before',
			filterType: 'created_at',
		},
	];

	const searchParams = createAiChatFilters(filters);
	navigate(`/analytics/ai-chats?${searchParams}`);
}

/**
 * Navigates to AI chat analytics page
 */
export function navigateToAiChats(navigate: NavigateFunction): void {
	navigate('/analytics/ai-chats');
}

/**
 * Navigates to AI chat analytics with a time range filter (e.g., "last 30 days")
 * Converts relative time ranges to actual date ranges
 */
export function navigateToAiChatsWithTimeRange(
	navigate: NavigateFunction,
	timeRange: string
): void {
	// Calculate the date range based on the time range
	const now = new Date();
	const [endDate] = now.toISOString().split('T');

	let startDate: string;

	// Parse the time range (e.g., "7d", "30d", "90d")
	const match = timeRange.match(/^(\d+)d$/);
	if (match) {
		const days = parseInt(match[1], 10);
		const start = new Date(now);
		start.setDate(start.getDate() - days);
		[startDate] = start.toISOString().split('T');
	} else {
		// Default to last 30 days if format is unexpected
		const start = new Date(now);
		start.setDate(start.getDate() - 30);
		[startDate] = start.toISOString().split('T');
	}

	const filters: DateFilterParams[] = [
		{
			value: [startDate, endDate],
			operator: 'is between',
			filterType: 'created_at',
		},
	];

	const searchParams = createAiChatFilters(filters);
	navigate(`/analytics/ai-chats?${searchParams}`);
}
